import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MAX_POSTS = 5;

const emptyForm = { image_url: '', post_url: '', caption: '', position: 0 };

const AdminInstagram = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!isAuthenticated() || user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram/admin/posts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setPosts(await res.json());
      }
    } catch {
      toast.error(t('adminInstagram.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingPost(null);
    setForm({ ...emptyForm, position: posts.filter(p => p.is_active).length });
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      image_url: post.image_url,
      post_url: post.post_url,
      caption: post.caption || '',
      position: post.position,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url.trim() || !form.post_url.trim()) {
      toast.error(t('adminInstagram.errorRequired'));
      return;
    }
    setSaving(true);
    try {
      const url = editingPost
        ? `${API_URL}/api/instagram/admin/posts/${editingPost.id}`
        : `${API_URL}/api/instagram/admin/posts`;
      const method = editingPost ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          image_url: form.image_url.trim(),
          post_url: form.post_url.trim(),
          caption: form.caption.trim() || null,
          position: Number(form.position),
        }),
      });
      if (res.ok) {
        toast.success(editingPost ? t('adminInstagram.toastUpdated') : t('adminInstagram.toastAdded'));
        localStorage.removeItem('mr_ig_posts_cache');
        setShowForm(false);
        fetchPosts();
      } else {
        const err = await res.json();
        toast.error(err.detail || t('adminInstagram.errorSave'));
      }
    } catch {
      toast.error(t('adminInstagram.errorNetwork'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (post) => {
    try {
      const res = await fetch(`${API_URL}/api/instagram/admin/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !post.is_active }),
      });
      if (res.ok) {
        localStorage.removeItem('mr_ig_posts_cache');
        fetchPosts();
      } else {
        const err = await res.json();
        toast.error(err.detail || t('adminInstagram.errorUpdate'));
      }
    } catch {
      toast.error(t('adminInstagram.errorNetwork'));
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm(t('adminInstagram.confirmDelete'))) return;
    try {
      const res = await fetch(`${API_URL}/api/instagram/admin/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok || res.status === 204) {
        toast.success(t('adminInstagram.toastDeleted'));
        localStorage.removeItem('mr_ig_posts_cache');
        fetchPosts();
      }
    } catch {
      toast.error(t('adminInstagram.errorNetwork'));
    }
  };

  const activePosts = posts.filter(p => p.is_active);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900">
              📸 {t('adminInstagram.title')}
            </h1>
            <p className="text-gray-500 mt-1">
              {activePosts.length}/{MAX_POSTS} {t('adminInstagram.subtitle')}
              {activePosts.length === 0 && (
                <span className="ml-2 text-yellow-600 font-medium">
                  {t('adminInstagram.noPostsWarning')}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={openAdd}
            disabled={activePosts.length >= MAX_POSTS}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + {t('adminInstagram.addPost')}
          </button>
        </motion.div>

        {/* Add / Edit Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6 mb-6 border-2 border-racing-red"
          >
            <h2 className="text-xl font-bold mb-4">
              {editingPost ? t('adminInstagram.editPostTitle') : t('adminInstagram.addNewPost')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('adminInstagram.imageUrl')} <span className="text-racing-red">*</span>
                </label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder={t('adminInstagram.imageUrlPlaceholder')}
                  className="input w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('adminInstagram.imageUrlHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('adminInstagram.postUrl')} <span className="text-racing-red">*</span>
                </label>
                <input
                  type="url"
                  value={form.post_url}
                  onChange={e => setForm(f => ({ ...f, post_url: e.target.value }))}
                  placeholder="https://www.instagram.com/p/..."
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('adminInstagram.caption')}
                </label>
                <input
                  type="text"
                  value={form.caption}
                  onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                  placeholder={t('adminInstagram.captionPlaceholder')}
                  className="input w-full"
                  maxLength={300}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('adminInstagram.position')}
                </label>
                <input
                  type="number"
                  min={0}
                  max={MAX_POSTS - 1}
                  value={form.position}
                  onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                  className="input w-24"
                />
              </div>

              {/* Image preview */}
              {form.image_url && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">{t('adminInstagram.preview')}</p>
                  <img
                    src={form.image_url}
                    alt="preview"
                    className="h-40 w-40 object-cover rounded-lg border"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {saving ? t('adminInstagram.saving') : editingPost ? t('adminInstagram.saveChanges') : t('adminInstagram.addPost')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn btn-outline"
                >
                  {t('adminInstagram.cancel')}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Posts list */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card p-4 animate-pulse flex gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="card p-12 text-center text-gray-500">
            <p className="text-lg mb-2">{t('adminInstagram.noPostsTitle')}</p>
            <p className="text-sm">{t('adminInstagram.noPostsDesc', { max: MAX_POSTS })}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card p-4 flex items-center gap-4 ${!post.is_active ? 'opacity-50' : ''}`}
              >
                {/* Thumbnail */}
                <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img
                    src={post.image_url}
                    alt={post.caption || t('adminInstagram.title')}
                    className="w-20 h-20 object-cover rounded-lg border"
                    onError={e => { e.target.src = 'https://placehold.co/80x80?text=?'; }}
                  />
                </a>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {post.caption || <span className="text-gray-400 italic">{t('adminInstagram.noCaption')}</span>}
                  </p>
                  <a
                    href={post.post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:underline truncate block"
                  >
                    {post.post_url}
                  </a>
                  <p className="text-xs text-gray-400 mt-1">{t('adminInstagram.positionLabel')}: {post.position}</p>
                </div>

                {/* Status badge */}
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${
                    post.is_active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {post.is_active ? t('adminInstagram.active') : t('adminInstagram.hidden')}
                </span>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(post)}
                    className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
                  >
                    {t('adminInstagram.edit')}
                  </button>
                  <button
                    onClick={() => toggleActive(post)}
                    className={`text-xs px-3 py-1 rounded-lg font-medium transition ${
                      post.is_active
                        ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
                        : 'bg-green-100 hover:bg-green-200 text-green-700'
                    }`}
                  >
                    {post.is_active ? t('adminInstagram.hide') : t('adminInstagram.show')}
                  </button>
                  <button
                    onClick={() => deletePost(post.id)}
                    className="text-xs px-3 py-1 bg-red-100 hover:bg-red-200 text-racing-red rounded-lg font-medium transition"
                  >
                    {t('adminInstagram.delete')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInstagram;
